import { z } from 'zod';
import { isCountryCode, normalizeCountryCode } from "./countries.js";
/**
 * A player's number. Eight digits, issued in order: the first person to
 * register is `00000001`, the second `00000002`.
 *
 * Sequential rather than random, and digits rather than letters, because this
 * is a membership number and that is what a membership number looks like. It
 * is read aloud across a room, typed on a phone keypad, and written on a card,
 * and every one of those is easier with ten symbols than with thirty-two. The
 * width is fixed at eight so that the numbers sort and line up in a list, and
 * eight digits is a hundred million members — a limit no club will meet.
 *
 * Counting starts at one rather than nought: `00000000` is what an empty
 * field and a failed parse both look like, and the first member should not
 * have to keep explaining that their number is a real one.
 *
 * It is not a secret and never was: knowing somebody's number gets an attacker
 * exactly as far as knowing their name, which is nowhere without the password.
 * That it is guessable is therefore not a weakness but the point — it is an
 * identifier, and the sign-in throttle is what makes guessing pointless.
 */
export const PUBLIC_ID_LENGTH = 8;
export function formatPublicId(n) {
    return String(n).padStart(PUBLIC_ID_LENGTH, '0');
}
export const PublicIdSchema = z
    .string()
    .trim()
    .regex(new RegExp(`^[0-9]{${PUBLIC_ID_LENGTH}}$`), 'a player number is 8 digits');
/**
 * The handle a player signs in with: their first name, a `#`, and their
 * public ID — `Alex#00000042`.
 *
 * A player number alone is eight digits of nothing, and people were reading it
 * off a card every time because there was nothing in it to remember. Sticking
 * the name on the front makes it recognisable and, more to the point, makes it
 * *distinguishable*: numbers issued in order look alike, and `Alex#00000042`
 * is a great deal harder to mistake for `Bo#00000043` than the bare numbers
 * are for each other.
 *
 * The name half is fixed at registration and never follows a later rename. A
 * login that changes underneath somebody is a support call, and the ID half is
 * what actually identifies the account anyway.
 */
export const USERNAME_SEPARATOR = '#';
/**
 * The printable part of a first name, for use in a handle.
 *
 * Accents are folded rather than dropped (`Renée` -> `Renee`), because the
 * player has to be able to type their own handle on a scoreboard whose
 * keyboard may not have their language on it.
 */
export function usernameSlug(firstName) {
    const folded = firstName
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]/g, '');
    return folded.slice(0, 20) || 'Player';
}
export function usernameFor(firstName, publicId) {
    return `${usernameSlug(firstName)}${USERNAME_SEPARATOR}${publicId}`;
}
/**
 * What may be typed into the sign-in box: a username, or a bare player ID.
 *
 * The ID is still accepted because it is printed on membership cards and in
 * every older screenshot, and because rejecting it would mean an account that
 * cannot sign in from a card that says it can.
 */
export const LoginNameSchema = z.string().trim().min(1).max(80);
/** Email is the only way a forgotten username can be recovered, so it is required. */
export const EmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .email('that does not look like an email address');
/** Stored upper-case, but accepted in any casing so a client need not care. */
const CountryCodeSchema = z
    .string()
    .trim()
    .refine(isCountryCode, { message: 'unknown country' })
    .transform((code) => normalizeCountryCode(code));
const NameSchema = z.string().trim().min(1).max(60);
/**
 * At least 8 characters mixing letters and digits.
 *
 * The upper bound exists because scrypt hashes whatever it is given, so an
 * unbounded password is an easy way to burn server CPU.
 */
export const PasswordSchema = z
    .string()
    .min(8, 'at least 8 characters')
    .max(200)
    .regex(/[A-Za-z]/, 'must contain a letter')
    .regex(/[0-9]/, 'must contain a digit');
export const ClubSchema = z.object({
    name: z.string().trim().min(1).max(120),
    country: CountryCodeSchema,
    city: z.string().trim().min(1).max(120),
});
export const RegisterPlayerSchema = z.object({
    firstName: NameSchema,
    lastName: NameSchema,
    email: EmailSchema,
    country: CountryCodeSchema,
    club: ClubSchema.optional(),
    password: PasswordSchema,
});
/**
 * Sign-in takes a `username`, but a body that still says `playerId` is read as
 * one: an older phone with the app cached must not be signed out by a deploy.
 */
export const LoginSchema = z.preprocess((value) => {
    if (typeof value !== 'object' || value === null)
        return value;
    const body = value;
    if (body['username'] === undefined && typeof body['playerId'] === 'string') {
        return { ...body, username: body['playerId'] };
    }
    return body;
}, z.object({
    username: LoginNameSchema,
    password: z.string().min(1).max(200),
}));
export const UpdatePlayerSchema = z
    .object({
    firstName: NameSchema.optional(),
    lastName: NameSchema.optional(),
    country: CountryCodeSchema.optional(),
    club: ClubSchema.nullable().optional(),
    /**
     * The account's own password, required for the change to be made.
     *
     * A session is not proof on a club tablet: the board sits on a table with
     * two accounts signed into it, and anyone walking past could otherwise
     * rename somebody else's club. Knowing the password is the only thing that
     * separates the owner from a passer-by.
     */
    password: z.string().min(1).max(200),
})
    .refine((v) => Object.keys(v).length > 1, { message: 'nothing to update' });
/** Proving the password again before a change is made. */
export const ConfirmPasswordSchema = z.object({
    password: z.string().min(1).max(200),
});
/**
 * The word a player has to type out to close their account.
 *
 * Kept in the contracts so the server can insist on it too: a confirmation
 * that only the button knows about is a confirmation an API call skips.
 */
export const DELETE_CONFIRMATION = 'DELETE';
/** Closing an account requires the password, so a walk-up device cannot do it. */
export const DeleteAccountSchema = z.object({
    password: z.string().min(1).max(200),
    /**
     * Typed out in full, in capitals. A password is muscle memory and a red
     * button is one tap; spelling the word out is the deliberate act that a
     * permanent, unrecoverable deletion deserves.
     */
    confirm: z.literal(DELETE_CONFIRMATION),
});
export function playerDisplayName(p) {
    return `${p.firstName} ${p.lastName}`.trim();
}
/** Avatar limits, shared so the client can reject a file before uploading it. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
//# sourceMappingURL=players.js.map